import React, { useEffect, useState, useCallback } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import NotificationsIcon from '@material-ui/icons/Notifications';
import EventNoteIcon from '@material-ui/icons/EventNote';
import moment from 'moment-timezone';
import { asyncListAll, request } from 'utilities/graph';
import { getElderServiceEventsByClientByType } from 'graphql/queries';
import { updateElderServiceEvent } from 'graphql/mutations';
import cache from 'utilities/cache';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import { sortBy } from 'utilities/sorting';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingReminderId, setUpdatingReminderId] = useState(null);

  const clientId = cache.get('app:facilityId');

  const fetchReminders = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const now = moment().tz(TIME_ZONE).toISOString();
      const elderServiceEvents = await asyncListAll(getElderServiceEventsByClientByType, {
        clientId,
        type: { eq: 'reminder' },
        filter: {
          and: [
            {
              startedAt: { lt: now },
            },
            {
              status: { eq: '使用中' },
            },
          ],
        },
      });

      const uniqueElderIds = [...new Set(elderServiceEvents.map((event) => event.elderId))];

      const elderData = {};
      await Promise.all(
        uniqueElderIds.map(async (elderId) => {
          try {
            const { data: { getElder: elder } } = await request(
              `query GetElder($id: ID!) {
                getElder(id: $id) {
                  id
                  name
                }
              }`,
              { id: elderId },
            );
            elderData[elderId] = elder;
          } catch (error) {
            console.error(`取得 elder ${elderId} 資料失敗:`, error);
            elderData[elderId] = { name: '沒有送餐對象' };
          }
        }),
      );

      const remindersWithElderNames = elderServiceEvents.map((event) => ({
        ...event,
        elderName: elderData[event.elderId]?.name || '沒有送餐對象',
      }));

      setReminders(remindersWithElderNames.sort(sortBy('startedAt', true)));
    } catch (e) {
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleToggleReminder = async (reminderId, currentStatus) => {
    if (!reminderId) return;

    setUpdatingReminderId(reminderId);
    try {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');
      const newStatus = currentStatus === '使用中' ? '已停用' : '使用中';

      await request(updateElderServiceEvent, {
        input: {
          id: reminderId,
          status: newStatus,
          updatedAt: now,
          updatedBy: userName,
        },
      });

      setReminders(reminders.map((item) => (item.id === reminderId ? { ...item, status: newStatus } : item)));
    } catch (error) {
      console.error('更新提醒狀態失敗:', error);
    } finally {
      setUpdatingReminderId(null);
    }
  };

  useEffect(() => {
    fetchReminders();
    const interval = setInterval(fetchReminders, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchReminders]);

  return (
    <>
      <IconButton color="inherit" onClick={() => setOpen(true)} aria-label="提醒通知">
        <Badge badgeContent={reminders.filter((item) => item.status === '使用中').length} color="secondary">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <EventNoteIcon style={{ color: '#64b5f6', marginRight: 8, verticalAlign: 'middle' }} />
          提醒通知
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Typography align="center">載入中...</Typography>
          ) : reminders.length === 0 ? (
            <Typography align="center">目前沒有任何提醒通知</Typography>
          ) : (
            <List>
              {reminders.map((item) => (
                <ListItem key={item.id} alignItems="flex-start">
                  <ListItemText
                    primary={
                      <>
                        <Typography variant="body2" color="textSecondary">
                          {moment(item.startedAt).tz(TIME_ZONE).format('YYYY-MM-DD dddd')}
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: '#64b5f6', fontWeight: 600 }}>
                          {item.elderName } - {item.description || '（沒有內容）'}
                        </Typography>
                      </>
                    }
                    secondary={
                      item.note && (
                        <Typography variant="body2" color="textSecondary">
                          備註：{item.note}
                        </Typography>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="textSecondary" style={{ marginRight: 8 }}>
                      {item.status === '使用中' ? '開啟' : '關閉'}
                    </Typography>
                    <Switch
                      checked={item.status === '使用中'}
                      onChange={() => handleToggleReminder(item.id, item.status)}
                      disabled={updatingReminderId === item.id}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">關閉</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
